import { useEffect, useRef } from 'react';

import { usePixelLoader } from '~/hooks/usePixelLoader';

import { getGoogleConsentState, updateGoogleConsent } from './googleConsentMode';

interface PixelProps {
  pixelId: string;
  marketingCookies: boolean;
  consentModeEnabled: boolean;
}

const GA4Pixel = ({ pixelId, marketingCookies, consentModeEnabled }: PixelProps) => {
  const trimmedId = pixelId?.trim();
  const validPixelId = Boolean(trimmedId?.match(/^G-[A-Z0-9]{8,}$/));
  const shouldLoadPixel = consentModeEnabled || marketingCookies;
  const marketingCookiesRef = useRef(marketingCookies);
  const lastConsentRef = useRef<boolean | undefined>(undefined);

  marketingCookiesRef.current = marketingCookies;

  useEffect(() => {
    if (!consentModeEnabled || !validPixelId || !trimmedId) {
      lastConsentRef.current = undefined;
      return;
    }

    if (document.getElementById('ga-pixel')) {
      return;
    }

    const initialMarketingConsent = marketingCookiesRef.current;
    const script = document.createElement('script');
    script.id = 'ga-pixel';
    script.innerHTML = `
      (function(){try{
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('consent', 'default', ${JSON.stringify(getGoogleConsentState(false))});
      gtag('consent', 'update', ${JSON.stringify(getGoogleConsentState(initialMarketingConsent))});
      gtag('js', new Date());
      gtag('config', '${trimmedId}', {
        page_path: window.location.pathname,
      });
      }catch{/*swallow exception*/}})();
    `;
    document.head.appendChild(script);
    lastConsentRef.current = initialMarketingConsent;

    return () => {
      script.remove();
      lastConsentRef.current = undefined;
    };
  }, [consentModeEnabled, trimmedId, validPixelId]);

  useEffect(() => {
    if (!consentModeEnabled || !validPixelId || lastConsentRef.current === marketingCookies) {
      return;
    }

    updateGoogleConsent(marketingCookies);
    lastConsentRef.current = marketingCookies;
  }, [consentModeEnabled, marketingCookies, validPixelId]);

  usePixelLoader(() => {
    if (!validPixelId || !trimmedId || !shouldLoadPixel) {
      return;
    }

    if (document.getElementById('ga-pixel-loader')) {
      return;
    }

    const loaderScript = document.createElement('script');
    loaderScript.id = 'ga-pixel-loader';
    loaderScript.async = true;
    loaderScript.src = `https://www.googletagmanager.com/gtag/js?id=${trimmedId}`;
    document.head.appendChild(loaderScript);

    let configScript: HTMLScriptElement | undefined;

    if (!consentModeEnabled) {
      configScript = document.createElement('script');
      configScript.async = true;
      configScript.id = 'ga-pixel';
      configScript.innerHTML = `
        (function(){try{
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${trimmedId}', {
          page_path: window.location.pathname,
        });
        }catch{/*swallow exception*/}})();
      `;
      document.head.appendChild(configScript);
    }

    return () => {
      loaderScript.remove();
      configScript?.remove();
    };
  }, [consentModeEnabled, shouldLoadPixel, trimmedId, validPixelId]);

  return null;
};

export default GA4Pixel;
